import SectionRecord from "models/view-models/section-record";
import { useCallback, useEffect } from "react";
import ServiceHookResult from "utilities/interfaces/service-hook-result";
import { ToastManager } from "utilities/toast/toast-manager";
import { t } from "utilities/localization-utils";
import useServiceHookResultState from "utilities/hooks/use-service-hook-result-state";
import useCurrentPublication from "utilities/contexts/use-current-publication";
import SectionService from "utilities/services/sections/section-service";
interface UseSectionsByExternalIdOptions {
    enabled?: boolean;
    sectionId?: number;
}

interface UseSectionsByExternalIdHook extends ServiceHookResult<SectionRecord> {
    refresh: () => void;
}

/**
 * Gets a section and all subSections by the section's externalId,
 * scoped to be used across publications
 * resolves to an externalId hash rather than a live sectionId.
 */

export default function useLatestSection(
    options: UseSectionsByExternalIdOptions
): UseSectionsByExternalIdHook {
    const { enabled = true, sectionId } = options;

    const { list: listSectionsApi } = SectionService.useList();

    const { result, setError, setInitial, setLoading, setSuccess } =
        useServiceHookResultState<SectionRecord>(new SectionRecord());

    const { publication } = useCurrentPublication();

    const load = useCallback(async () => {
        if (sectionId == null || publication?.id == null || !enabled) {
            setInitial();
            return;
        }

        setLoading();
        try {
            const sectionResponse = await listSectionsApi({
                sectionIds: [sectionId],
                useLatest: true,
            });
            const section = sectionResponse.resultObjects[0];
            if (section == null) {
                setError(t("sections-include-subsections-not-found"));

                return;
            }

            const subSectionResponse = await listSectionsApi({
                parentId: section.id,
            });

            const subSectionsWithPublications =
                subSectionResponse.resultObjects.map(
                    (subSection: SectionRecord) =>
                        subSection.with({
                            publication: publication,
                        })
                );

            const sectionWithSubSectionsAndPublication = section.with({
                subSections: subSectionsWithPublications,
                publication: publication,
            });

            setSuccess(sectionWithSubSectionsAndPublication);
        } catch (error) {
            ToastManager.error(t("sections-include-subsection-loading-error"));

            setError(error);
        }
    }, [
        enabled,
        sectionId,
        listSectionsApi,
        publication,
        setError,
        setInitial,
        setLoading,
        setSuccess,
    ]);

    const refresh = useCallback(() => load(), [load]);

    useEffect(() => {
        load();
    }, [load]);

    return {
        ...result,
        refresh,
    };
}
